/**
 * Public Upload Service
 * TASK-361: Document Management & Compliance System
 *
 * Service for the public upload portal (unauthenticated).
 * All API calls go through /public/upload endpoints without auth tokens.
 */

import axios from 'axios';
import type {
  PortalInfo,
  VerifyOTPResponse,
  UploadResponse,
  DocType,
} from '../types/documentCompliance';
import { API_CONFIG } from '../config/api-config';

// Create a separate axios instance for public endpoints (no auth)
const publicApi = axios.create({
  baseURL: API_CONFIG.GATEWAY_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

class PublicUploadService {
  private readonly basePath = '/public/upload/portal';

  /**
   * Get portal information for a given token.
   * Returns masked client email, document types, and upload limits.
   */
  async getPortalInfo(token: string): Promise<PortalInfo> {
    const response = await publicApi.get<PortalInfo>(`${this.basePath}/${token}`);
    return response.data;
  }

  /**
   * Request an OTP to be sent to the client's email.
   * Rate limited: 3 per hour per token.
   */
  async requestOTP(token: string): Promise<{ success: boolean; message: string }> {
    const response = await publicApi.post(`${this.basePath}/${token}/request-otp`);
    return response.data;
  }

  /**
   * Verify the OTP code entered by the client.
   * Returns a session token on success.
   */
  async verifyOTP(token: string, otpCode: string): Promise<VerifyOTPResponse> {
    const response = await publicApi.post<VerifyOTPResponse>(
      `${this.basePath}/${token}/verify-otp`,
      { otp_code: otpCode }
    );
    return response.data;
  }

  /**
   * Upload a file to the quarantine area.
   * Requires a valid session token from OTP verification.
   */
  async uploadFile(
    token: string,
    sessionToken: string,
    file: File,
    docType: DocType,
    onProgress?: (progress: number) => void
  ): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docType);

    const response = await publicApi.post<UploadResponse>(
      `${this.basePath}/${token}/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Session-Token': sessionToken,
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      }
    );
    return response.data;
  }

  /**
   * Validate a file before upload.
   * Checks file type, size, and extension.
   */
  validateFile(file: File): { valid: boolean; error?: string } {
    const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
    const MIN_FILE_SIZE = 100; // 100 bytes

    const ALLOWED_TYPES = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/heic',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
      // Word documents (.docx) — editable in the Document Workbench (TASK-449)
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      // PowerPoint decks (.pptx) — editable in the PowerPoint Workbench (TASK-444)
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ];

    const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.heic', '.xlsx', '.xls', '.csv', '.docx', '.pptx'];

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: 'File size exceeds 20MB limit' };
    }

    if (file.size < MIN_FILE_SIZE) {
      return { valid: false, error: 'File appears to be empty or corrupted' };
    }

    // Check MIME type
    if (!ALLOWED_TYPES.includes(file.type)) {
      return { valid: false, error: `File type "${file.type}" is not allowed` };
    }

    // Check extension
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      return { valid: false, error: `File extension "${extension}" is not allowed` };
    }

    return { valid: true };
  }
}

export const publicUploadService = new PublicUploadService();
